import {z} from 'zod';

import {IsoUtcDateTimeSchema, UuidV4Schema} from './common.js';
import {
  EDITORIAL_EXCERPT_MAX_CHARS,
  EDITORIAL_PROSE_MAX_CHARS,
  EditorialFindingSchema,
  EditorialPendencySchema,
  EditorialRepetitionSchema,
  EditorialSourceSupportSchema,
} from './editorial-limits.js';
import type {
  GeneratedContent,
  GenerationUsage,
  SanitizedEvidence,
} from './generation.js';
import {GenerationUsageSchema} from './generation.js';

/**
 * O PARECER EDITORIAL, e por que ele é um contrato à parte.
 *
 * `GeneratedContentSchema` é `strictObject`: um campo a mais reprova a geração
 * inteira. Enfiar nota, justificativa e achado ali dentro não sujaria o
 * contrato, derrubaria a peça no schema. E há um motivo mais fundo: o conteúdo é
 * o que vai ao ar, o parecer é o que alguém achou dele. Guardar os dois no mesmo
 * objeto faria a opinião viajar junto com a publicação para todo lugar que lê
 * uma peça, inclusive o manifesto que o dono assina.
 *
 * ## O que este parecer NÃO é
 *
 * Ele não é decisão. `recommendation` tem `blocked` porque um editor pode dizer
 * "eu não publicaria isto", e essa frase precisa caber em algum lugar. Ela NÃO
 * bloqueia peça: quem bloqueia é portão, e portão tem enum próprio, tabela
 * própria e sete linhas gravadas por versão. Um parecer favorável sobre peça
 * bloqueada continua sendo uma peça bloqueada, e o teste da esteira prova.
 *
 * ## Saída de modelo é entrada não confiável, inclusive esta
 *
 * O revisor leu o mesmo material que o escritor, e material de fora carrega
 * instrução escrita para modelo. Por isso tudo aqui é fechado e medido: campo a
 * mais reprova, texto tem teto, e a lista de critérios tem tamanho e ORDEM
 * cobrados. O achado do revisor chega ao escritor na correção, e o que impede
 * um achado plantado de mudar a peça é a fixação de âncoras, do outro lado.
 */

// ---------------------------------------------------------------------------
// Os limites, o achado, a pendência e os dois vereditos: em `editorial-limits.ts`
// ---------------------------------------------------------------------------
//
// Eles nasceram aqui e saíram em 2026-09-06, pelo achado F07 da auditoria: o
// contrato da CORREÇÃO (`GenerationRevisionSchema`, em `generation.ts`) tinha
// uma cópia escrita à mão dos mesmos campos com teto de trezentos, e um parecer
// válido de 400 caracteres derrubava a correção no schema antes de a chamada ao
// escritor sair. Trazer o parecer para dentro de `generation.ts` fecharia um
// ciclo de módulo (este arquivo já importa o uso e o conteúdo de lá), então os
// números desceram para um arquivo que os dois lados importam.
//
// Eles continuam saindo do pacote pelo `index.ts`, e quem os importava de
// `@devmil/contracts` não muda uma linha.
//
// A PENDÊNCIA e os DOIS VEREDITOS desceram pelo mesmo caminho em 2026-09-07,
// quando o pedido de correção passou a carregá-los: os três reprovam a peça
// independentemente da nota, e nenhum deles chegava ao escritor. O motivo de
// moradia é o mesmo ciclo de módulo, e a medição que os moveu está escrita lá.

// ---------------------------------------------------------------------------
// As cinco dimensões
// ---------------------------------------------------------------------------

/**
 * As cinco dimensões da régua, na ORDEM em que o parecer as lista.
 *
 * A ordem é parte do contrato, e não estilo: a tela desenha as cinco em linha, e
 * um parecer com as notas embaralhadas mostraria a nota da entrega prática
 * embaixo do rótulo do gancho sem nada denunciar.
 */
export const EDITORIAL_CRITERIA = [
  /** A peça fala de uma situação que ESTE público reconhece. */
  'audience_relevance',
  /** O gancho diz o assunto, e não o esconde para provocar curiosidade. */
  'hook_clarity',
  /** Sai dali uma descoberta, demonstração, orientação ou decisão concreta. */
  'practical_delivery',
  /** O desenvolvimento entrega o que a abertura prometeu. */
  'promise_coherence',
  /** O tamanho e o feitio cabem no formato da peça. */
  'format_fit',
] as const;

export const EditorialCriterionIdSchema = z.enum(EDITORIAL_CRITERIA);
export type EditorialCriterionId = z.infer<typeof EditorialCriterionIdSchema>;

/**
 * A escala, de 0 a 4.
 *
 * Zero é ausência ou contradição; 2 é entrega parcial; 4 é entrega clara e
 * específica. A escala é pequena de propósito: uma régua de 0 a 10 dá a
 * impressão de precisão que um julgamento editorial não tem, e faz duas peças
 * separadas por um ponto parecerem diferentes quando ninguém consegue defender a
 * diferença.
 */
export const EditorialScoreSchema = z.int().min(0).max(4);

/**
 * Uma nota, com o TRECHO que a sustenta.
 *
 * O trecho é anulável porque nota sobre ausência não tem trecho: "não existe
 * exemplo concreto" não pode apontar para lugar nenhum. O que não se aceita é
 * nota sem nada: quem quiser justificar por ausência escreve isso no achado.
 */
export const EditorialCriterionSchema = z.strictObject({
  id: EditorialCriterionIdSchema,
  score: EditorialScoreSchema,
  excerpt: z.string().trim().min(1).max(EDITORIAL_EXCERPT_MAX_CHARS).nullable(),
});
export type EditorialCriterion = z.infer<typeof EditorialCriterionSchema>;

// ---------------------------------------------------------------------------
// A recomendação do editor
// ---------------------------------------------------------------------------

export const EditorialRecommendationSchema = z.enum([
  /** Apto editorialmente. Não é aprovação, e não libera nada. */
  'fit',
  'rewrite',
  /** "Eu não publicaria isto." Não bloqueia peça: portão bloqueia peça. */
  'blocked',
]);
export type EditorialRecommendation = z.infer<
  typeof EditorialRecommendationSchema
>;

// ---------------------------------------------------------------------------
// O que o revisor devolve
// ---------------------------------------------------------------------------

export const EditorialReviewSchema = z
  .strictObject({
    recommendation: EditorialRecommendationSchema,
    criteria: z
      .array(EditorialCriterionSchema)
      .length(EDITORIAL_CRITERIA.length),
    findings: z.array(EditorialFindingSchema).max(10),
    /** A única mudança que o revisor faria, se pudesse fazer uma só. */
    mainChange: z
      .string()
      .trim()
      .min(1)
      .max(EDITORIAL_PROSE_MAX_CHARS)
      .nullable(),
    pendency: EditorialPendencySchema.nullable(),
    /**
     * OBRIGATÓRIOS, e não anuláveis com significado de "não olhei".
     *
     * O dono nomeou quatro coisas que o crítico precisa verificar, e duas delas
     * (repetição, e gancho contra fonte) não cabem nas cinco dimensões, que
     * falam da peça sozinha. Um campo opcional deixaria o modelo pular a
     * conferência em silêncio, que é o defeito que este agente existe para
     * combater. A ausência de material tem valor próprio dentro de cada um
     * (`no_history`, `no_sources`), e é assim que "não deu para olhar" continua
     * dizível sem virar omissão.
     */
    repetition: EditorialRepetitionSchema,
    sourceSupport: EditorialSourceSupportSchema,
  })
  .refine(
    (review) =>
      review.criteria.every(
        (criterio, indice) => criterio.id === EDITORIAL_CRITERIA[indice],
      ),
    {
      message:
        'criteria precisa listar as cinco dimensões, uma vez cada, na ordem de EDITORIAL_CRITERIA',
      path: ['criteria'],
    },
  );
export type EditorialReview = z.infer<typeof EditorialReviewSchema>;

// ---------------------------------------------------------------------------
// O parecer gravado
// ---------------------------------------------------------------------------

/**
 * O parecer como ele fica no banco: o que o revisor disse, mais o que o SERVIDOR
 * atribuiu.
 *
 * A separação é a mesma de `GenerationResult`: identidade, hash e versões são
 * responsabilidade de quem pediu a avaliação, e não de quem a produziu. O modelo
 * não escolhe qual versão está avaliando, e não declara a régua contra a qual
 * foi medido.
 *
 * `scope: 'text'` não é enfeite, e o motivo mudou em 2026-09-06.
 *
 * Na fase 1 ele foi justificado por uma afirmação ERRADA: "não existe mídia
 * renderizada em ambiente nenhum". Existe. A API não renderiza dentro do
 * processo, ela escreve o pedido no volume pela ponte
 * (`spool-media-renderer.ts`) e o container do renderizador devolve o MP4, o
 * JPEG da capa e o `.render.json`; o painel serve esses bytes por
 * `/v1/panel/content/:id/cover` e `/media`. O comentário de `media-renderer.ts`
 * envelheceu junto e diz o contrário do que o código faz.
 *
 * O alcance continua declarado, e agora ele separa DUAS avaliações que existem:
 * este parecer olhou o TEXTO, e `EditorialMediaOpinionSchema` olha a MÍDIA
 * FINAL. Um parecer que omitisse o próprio alcance deixaria o leitor supor que a
 * capa foi olhada quando ninguém a abriu.
 */
export const EditorialOpinionSchema = z.strictObject({
  contentVersionId: UuidV4Schema,
  contentVersion: z.int().positive(),
  /** Hash do texto avaliado. Texto mudou, este parecer não vale mais. */
  contentSha256: z.string().regex(/^[0-9a-f]{64}$/u),
  /**
   * A VERSÃO da regra que produziu `contentSha256`.
   *
   * Ela existe porque o cálculo mudou em 2026-09-06 (achado F09: a versão 1
   * colava fala e tela com ` | ` e ignorava a duração, então as duas edições que
   * uma correção mais faz não mudavam o hash). Sem esta coluna, subir a regra
   * faria todo parecer anterior virar "o texto mudou" em silêncio, e um
   * relatório de calibração compararia notas medidas por coisas diferentes.
   *
   * Parecer com versão anterior à atual é INCOMPATÍVEL, e não apagado: ele
   * continua no banco, o hash dele nunca bate com o texto de hoje, e o painel
   * diz que não há avaliação DESTE texto.
   */
  contentHashVersion: z.int().positive(),
  scope: z.literal('text'),
  mediaReviewed: z.literal(false),
  promptVersion: z.string().min(1).max(60),
  rubricVersion: z.string().min(1).max(60),
  provider: z.string().min(1).max(40),
  model: z.string().min(1).max(80),
  recommendation: EditorialRecommendationSchema,
  criteria: z.array(EditorialCriterionSchema).length(EDITORIAL_CRITERIA.length),
  findings: z.array(EditorialFindingSchema).max(10),
  mainChange: z
    .string()
    .trim()
    .min(1)
    .max(EDITORIAL_PROSE_MAX_CHARS)
    .nullable(),
  pendency: EditorialPendencySchema.nullable(),
  /**
   * Os dois vereditos estruturados. ANULÁVEIS aqui, e obrigatórios no que o
   * revisor devolve: parecer gravado antes da régua v2 não tem os campos, e
   * inventar `none` para essas linhas antigas diria que alguém conferiu
   * repetição numa peça que ninguém comparou com nada.
   */
  repetition: EditorialRepetitionSchema.nullable(),
  sourceSupport: EditorialSourceSupportSchema.nullable(),
  /** Soma das cinco notas, calculada pela régua e não pelo modelo. */
  total: z
    .int()
    .min(0)
    .max(EDITORIAL_CRITERIA.length * 4),
  meetsFloor: z.boolean(),
  /**
   * O TEXTO deste parecer é uma reescrita.
   *
   * Ele responde sobre a versão ENTREGUE, e não sobre a peça: com o laço de
   * correção (2026-09-07) uma peça pode receber três reescritas e mesmo assim
   * entregar o texto original, quando nenhuma delas melhorou a nota. Nesse caso
   * `corrected` é falso e `correctionAttempts` é três, e os dois estão certos.
   */
  corrected: z.boolean(),
  /**
   * Quantas reescritas a PEÇA recebeu antes deste parecer.
   *
   * Zero é o caso normal e o único possível no modo `parecer`. Ele conta as
   * tentativas DESCARTADAS junto com a que venceu: elas aconteceram, custaram e
   * são o que explica um `calls` alto. Sem este número, "corrigida uma vez" era
   * a única frase que o painel sabia dizer, e ela virou mentira no dia em que a
   * segunda correção passou a existir.
   */
  correctionAttempts: z.int().nonnegative(),
  /**
   * Chamadas pagas gastas para chegar a este parecer, incluindo as tentativas
   * técnicas da geração e as chamadas descartadas. É o número que o teto por
   * peça compara, e é o único jeito de o gasto não crescer sem ninguém ver.
   */
  calls: z.int().positive(),
  /** Micros. ANULÁVEL: preço não configurado é desconhecido, e não zero. */
  costMicros: z.int().nonnegative().nullable(),
  latencyMs: z.int().nonnegative(),
  evaluatedAt: IsoUtcDateTimeSchema,
});
export type EditorialOpinion = z.infer<typeof EditorialOpinionSchema>;

// ---------------------------------------------------------------------------
// A porta
// ---------------------------------------------------------------------------

/**
 * O que o revisor recebe.
 *
 * Repare no que NÃO está aqui: o piso da régua, a recomendação desejada, e
 * qualquer sinal de que existe uma correção depois. Um revisor que soubesse a
 * nota que o escritor quer não é revisor, é carimbo. É a cerca central deste
 * desenho, e ela tem teste próprio.
 *
 * `recentPieces` vazio é uma informação, e não a ausência dela: o pedido diz ao
 * revisor que não há histórico, para ele não afirmar repetição por chute.
 */

/**
 * Uma peça recente da mesma série, como o crítico a enxerga.
 *
 * O gancho E o título da pauta, e não só o gancho: a fase 1 mandava apenas
 * ganchos, e repetição de ASSUNTO some quando dois textos abrem de jeitos
 * diferentes sobre a mesma pauta. O título é o que separa "abriu igual" de
 * "falou da mesma coisa", e o dono pediu as duas.
 *
 * `decidedAt` viaja para o revisor poder dizer QUAL peça a nova repete, em vez
 * de "uma das recentes".
 */
export interface EditorialRecentPiece {
  hook: string;
  /** Nulo quando a peça não tem pauta vinculada. Não é string vazia. */
  topicTitle: string | null;
  /** Data local `YYYY-MM-DD` da decisão do dono sobre aquela peça. */
  decidedAt: string;
}

export interface EditorialReviewInput {
  content: GeneratedContent;
  topicTitle: string;
  premise: string;
  evidence: readonly SanitizedEvidence[];
  recentPieces: readonly EditorialRecentPiece[];
}

export const EditorialReviewResultSchema = z.strictObject({
  provider: z.string().min(1).max(40),
  model: z.string().min(1).max(80),
  promptVersion: z.string().min(1).max(60),
  review: EditorialReviewSchema,
  usage: GenerationUsageSchema,
  latencyMs: z.int().nonnegative(),
});
export type EditorialReviewResult = z.infer<typeof EditorialReviewResultSchema>;

/**
 * O adaptador da revisão.
 *
 * Porta, e não chamada direta ao gerador de texto, pelo mesmo motivo do leitor
 * do radar: `GenerationInput` foi desenhado para PRODUZIR peça (pauta, premissa,
 * série, público, objetivo, guia de voz, evidência), e torcer aquele contrato
 * para descrever "julgue este texto" faria os dois usos brigarem pelo mesmo
 * schema. Aqui a entrada é outra, a saída é outra, e o custo é contado à parte.
 */
export interface EditorialReviewer {
  readonly provider: string;
  review(input: EditorialReviewInput): Promise<EditorialReviewResult>;
}

// ---------------------------------------------------------------------------
// A CONTABILIDADE DAS CHAMADAS PAGAS
// ---------------------------------------------------------------------------

/**
 * PARA QUE cada chamada paga serviu.
 *
 * Até 2026-09-06 a esteira só tinha um número agregado (`calls`, dentro do
 * parecer), e o achado F08 da auditoria mediu o que se perde nele: uma tentativa
 * de geração recusada pela política, uma correção que falhou, um timeout, cada
 * um sumia de um jeito. Um número somado não responde "quanto custou a revisão
 * visual deste mês", e é essa a pergunta que decide se o piloto continua.
 *
 * Lista de PERMISSÃO, e não texto livre: um motivo escrito à mão viraria vinte
 * grafias da mesma coisa no relatório de gasto.
 */
export const EDITORIAL_CALL_PURPOSES = [
  /** A escrita da peça, com as tentativas técnicas do laço de geração. */
  'base_generation',
  /** A avaliação do TEXTO pelo crítico editorial. */
  'text_review',
  /** A reescrita que o parecer pediu. */
  'correction',
  /** A avaliação da MÍDIA FINAL pelo crítico visual. */
  'media_review',
  /**
   * A FOTOGRAFIA de fundo da capa do carrossel.
   *
   * Finalidade própria, e não `base_generation` com outro provedor, porque ela
   * é a única linha do razão que não compra TEXTO: é uma chamada a um gerador
   * de imagem, com preço por token de saída de imagem e uma ordem de grandeza
   * própria (US$ 0,041 contra menos de um centavo por revisão). Somá-la à
   * escrita da peça faria o relatório do piloto perder a única pergunta que
   * decide se a capa fotográfica continua: quanto ela custou no mês.
   *
   * Ela entra no teto do dia como qualquer outra, e é isso que a separa da foto
   * do Reel, da Story e da peça de foto, que até hoje gastam FORA do razão.
   */
  'cover_image',
  /**
   * As fotografias de fundo dos outros SEIS slides do carrossel.
   *
   * Linha própria, e não somada a `cover_image`, porque as duas decisões são de
   * dias diferentes e podem ser desfeitas separadamente. A capa foi decidida na
   * manhã de 2026-09-07 ("as imagens são sempre realistas"); os seis slides,
   * na mesma tarde ("o carrosel eu queria sempre em imagem realística e quando
   * não houver pessoas junto queria um realista 3d"). Se o gasto incomodar, a
   * pergunta que o dono vai fazer é "quanto custaram os seis a mais", e um
   * número só não a responde.
   *
   * Elas entram no teto do dia como qualquer outra: seis chamadas a mais, folga
   * medida de 9 nas 40. Ver `0090_fotografia_em_todos_os_slides.sql`.
   */
  'slide_image',
  /**
   * A execução sob demanda de um dos doze módulos editoriais.
   *
   * Ela entra no MESMO teto diário das chamadas da esteira, e é de propósito: o
   * dono configurou um teto de gasto por dia, e não um teto por origem. O que a
   * finalidade separa é o RELATÓRIO, para o ensaio estratégico dele não somar
   * dentro do custo da esteira diária e fazer a peça parecer mais cara do que é.
   */
  'module_run',
] as const;
export const EditorialCallPurposeSchema = z.enum(EDITORIAL_CALL_PURPOSES);
export type EditorialCallPurpose = z.infer<typeof EditorialCallPurposeSchema>;

/**
 * O ESTADO de uma tentativa, e por que `discarded` não é `failed`.
 *
 * `failed` é a chamada que morreu: o provedor não respondeu, e o uso dela é
 * DESCONHECIDO. `discarded` é a chamada que respondeu, foi paga, e cujo
 * resultado foi jogado fora: o schema de casa recusou, ou a política reprovou a
 * peça. As duas custaram; só uma delas tem uso conhecido, e um relatório que as
 * misturasse não conseguiria explicar por que o total ficou nulo.
 *
 * `reserved` é o estado de nascimento, e ele conta no teto do dia. Uma linha
 * presa em `reserved` é uma chamada que saiu e cuja liquidação não voltou:
 * contá-la é o erro seguro, porque ela pode ter sido cobrada.
 */
export const EDITORIAL_CALL_STATES = [
  'reserved',
  'succeeded',
  'failed',
  'discarded',
] as const;
export const EditorialCallStateSchema = z.enum(EDITORIAL_CALL_STATES);
export type EditorialCallState = z.infer<typeof EditorialCallStateSchema>;

/**
 * O que a liquidação de uma chamada reservada carrega.
 *
 * `usage` nulo é "o provedor não devolveu uso", e é o caso normal de uma chamada
 * que morreu no meio. Ele NÃO vira zero: desconhecido contamina a soma, e o
 * total exato do dia fica indisponível em vez de sair menor que o real com
 * aparência de total.
 */
export interface EditorialCallSettlement {
  id: string;
  state: Exclude<EditorialCallState, 'reserved'>;
  usage: GenerationUsage | null;
  durationMs: number | null;
  /** Só o NOME do erro. Mensagem de cliente HTTP carrega cabeçalho. */
  errorName: string | null;
}

// ---------------------------------------------------------------------------
// A REVISÃO DA MÍDIA FINAL
// ---------------------------------------------------------------------------

/**
 * As três dimensões da mídia, e por que são três e não cinco.
 *
 * O dono escreveu o que quer conferido na peça pronta: "a peça deve funcionar no
 * mudo, com leitura confortável e coerência entre gancho, imagem, vídeo, legenda
 * e CTA". São três perguntas, e esticá-las para cinco só para casar com a régua
 * do texto produziria duas dimensões inventadas por simetria.
 *
 * A segunda merece um parágrafo, porque ela é a que mais convida ao erro.
 * `reading_comfort` NÃO remede contraste nem área segura: quem mede isso é o
 * portão de marca, com número, na página que o Chromium desenhou
 * (`brand-report.ts`). O crítico visual RECEBE o resultado daquele portão e
 * julga o que a medição não alcança: hierarquia, quantidade de texto na tela,
 * tempo de leitura por trecho. Duas medições da mesma coisa, uma delas por
 * opinião de modelo, é como um relatório passa a discordar de si mesmo.
 */
export const EDITORIAL_MEDIA_CRITERIA = [
  /** Dá para entender a peça sem som, só pelo que está escrito e desenhado. */
  'silent_comprehension',
  /** O texto na tela cabe, respira e dá tempo de ler. Cita o portão de marca. */
  'reading_comfort',
  /** Gancho, imagem, legenda e CTA contam a MESMA história. */
  'story_coherence',
] as const;
export const EditorialMediaCriterionIdSchema = z.enum(EDITORIAL_MEDIA_CRITERIA);
export type EditorialMediaCriterionId = z.infer<
  typeof EditorialMediaCriterionIdSchema
>;

export const EditorialMediaCriterionSchema = z.strictObject({
  id: EditorialMediaCriterionIdSchema,
  score: EditorialScoreSchema,
  /** O que na imagem sustenta a nota. Nulo quando a nota é sobre ausência. */
  excerpt: z.string().trim().min(1).max(EDITORIAL_EXCERPT_MAX_CHARS).nullable(),
});
export type EditorialMediaCriterion = z.infer<
  typeof EditorialMediaCriterionSchema
>;

/**
 * Onde o achado visual mora. Lista de PERMISSÃO, como a do texto.
 *
 * Ela é OUTRA lista de propósito: `hook` e `caption` são campos de texto, e um
 * achado visual sobre a legenda fala da relação dela com a imagem, não da
 * redação dela. Reusar o mesmo enum faria os dois pareceres apontarem para o
 * mesmo lugar dizendo coisas de naturezas diferentes.
 */
export const EDITORIAL_MEDIA_FINDING_FIELDS = [
  /** A capa do Reel, que é o primeiro quadro e o que aparece na grade. */
  'cover',
  /** A foto da peça de notícia, que É a mídia publicada. */
  'photo',
  /**
   * UM slide do carrossel, ou a relação entre eles.
   *
   * Campo próprio porque o achado de um carrossel tem endereço que os outros
   * formatos não têm: "o slide 4 repete o 2" e "a sequência não fecha" são
   * defeitos da ORDEM, e escrevê-los em `overall` faria o painel mostrar um
   * problema de sequência sem dizer onde olhar.
   */
  'slide',
  /** O texto desenhado sobre a imagem. */
  'screen_text',
  /** A relação entre o que a imagem mostra e o que a legenda promete. */
  'caption_vs_image',
  'cta',
  'overall',
] as const;
export const EditorialMediaFindingFieldSchema = z.enum(
  EDITORIAL_MEDIA_FINDING_FIELDS,
);

export const EditorialMediaFindingSchema = z.strictObject({
  field: EditorialMediaFindingFieldSchema,
  excerpt: z.string().trim().min(1).max(EDITORIAL_EXCERPT_MAX_CHARS).nullable(),
  issue: z.string().trim().min(1).max(EDITORIAL_PROSE_MAX_CHARS),
});
export type EditorialMediaFinding = z.infer<typeof EditorialMediaFindingSchema>;

export const EditorialMediaReviewSchema = z
  .strictObject({
    recommendation: EditorialRecommendationSchema,
    criteria: z
      .array(EditorialMediaCriterionSchema)
      .length(EDITORIAL_MEDIA_CRITERIA.length),
    findings: z.array(EditorialMediaFindingSchema).max(10),
    mainChange: z
      .string()
      .trim()
      .min(1)
      .max(EDITORIAL_PROSE_MAX_CHARS)
      .nullable(),
    pendency: EditorialPendencySchema.nullable(),
  })
  .refine(
    (review) =>
      review.criteria.every(
        (criterio, indice) => criterio.id === EDITORIAL_MEDIA_CRITERIA[indice],
      ),
    {
      message:
        'criteria precisa listar as três dimensões visuais, uma vez cada, na ordem de EDITORIAL_MEDIA_CRITERIA',
      path: ['criteria'],
    },
  );
export type EditorialMediaReview = z.infer<typeof EditorialMediaReviewSchema>;

/**
 * A imagem que o crítico visual vai olhar.
 *
 * Bytes, e nunca caminho. O crítico não abre arquivo: quem resolve identificador
 * para caminho é o armazenamento, com a lista de permissão dele, e mandar um
 * caminho para dentro de um adaptador que fala com a rede reabriria a porta que
 * `ObjectStorage` existe para fechar.
 *
 * `role` viaja porque muda o que se cobra: numa capa de Reel o texto na tela é o
 * assunto principal, numa peça de foto a imagem é a publicação inteira, e numa
 * AMOSTRA o que se cobra é aquela tela do vídeo, no lugar dela na ordem.
 *
 * `sample` entrou em 2026-09-06 (achado F11). Ela é um quadro representativo de
 * uma tela do Reel ou da Story, convertido em JPEG pelo renderizador e guardado
 * no volume `midia` ao lado do MP4. Ela NÃO é capa: não entra em
 * `media_objects`, não entra no manifesto e não muda `coverSha256`. Ver
 * `EditorialMediaCoverageSchema`, logo abaixo, e a migração 0083.
 */
export const EDITORIAL_MEDIA_ROLES = [
  'cover',
  'photo',
  'sample',
  /**
   * Um SLIDE do carrossel, que entrou em 2026-09-06.
   *
   * Ele não é `photo` nem `sample`, e a diferença muda o que se cobra. `photo` é
   * a publicação inteira, e o crítico julga a cena; `sample` é um quadro
   * representativo de um vídeo, e o crítico julga aquela tela no lugar dela no
   * TEMPO. Um slide é uma peça da SEQUÊNCIA: ele vai ao ar exatamente como
   * está, e o que se cobra dele é o texto legível e o encaixe na ordem — o
   * slide 4 precisa fazer sentido depois do 3 e antes do 5, e nenhuma das duas
   * outras funções tem essa pergunta.
   */
  'slide',
] as const;
export type EditorialMediaRole = (typeof EDITORIAL_MEDIA_ROLES)[number];

export interface EditorialMediaStill {
  role: EditorialMediaRole;
  /** O identificador do objeto, para o parecer poder apontar o que olhou. */
  objectId: string;
  sha256: string;
  mime: 'image/jpeg' | 'image/png';
  bytes: Uint8Array;
  /**
   * ONDE esta imagem fica na peça, quando ela é amostra de um vídeo.
   *
   * `null` na capa e na foto, que não têm lugar no tempo: a capa é o cartão de
   * entrada e a foto é a publicação inteira. Numa amostra ele é o que permite ao
   * pedido dizer "tela 2, dos 6,4 s aos 12,8 s" em vez de mandar três imagens
   * soltas e deixar o modelo supor a ordem — e supor a ordem é como um parecer
   * passa a falar de um ritmo que a peça não tem.
   */
  timing: {order: number; startMs: number; durationMs: number} | null;
  /**
   * ONDE este slide fica na sequência, quando a peça é um carrossel.
   *
   * `null` na capa, na foto e na amostra. Ele é o análogo de `timing` para uma
   * peça que não tem tempo: o carrossel não tem duração nenhuma, e reusar
   * `timing` com `startMs: 0` inventaria uma linha do tempo que a peça não tem,
   * o que o plano proíbe com todas as letras ("não inventar retenção por
   * slide").
   *
   * `total` viaja junto de `position` para o pedido poder dizer "slide 3 de 7"
   * mesmo quando só três slides foram abertos. Sem ele, um parecer sobre três
   * imagens soltas seria lido como parecer sobre a sequência.
   */
  slide: {position: number; total: number} | null;
}

/**
 * O QUE o crítico visual olhou, dito com todas as letras.
 *
 * Ela existe porque o achado F11 não foi "o parecer visual está errado": foi que
 * ele estava ESCONDIDO e PARCIAL, e o painel não tinha como distinguir "olhou a
 * capa" de "olhou a peça". Um parecer favorável sobre uma capa, lido como
 * parecer sobre o vídeo, é exatamente a nota favorável inventada que a regra 8
 * do plano proíbe.
 *
 * `videoReviewed` é `false` LITERAL, e não um booleano.
 *
 * Nenhum caminho desta esteira assiste ao vídeo. As amostras são quadros
 * parados: elas provam o que está desenhado em cada tela e não provam movimento,
 * transição, ritmo nem sincronia. Um campo booleano abriria a porta para alguém
 * escrever `true` no dia em que mandasse seis imagens em vez de uma, e seis
 * imagens paradas continuam não sendo o vídeo. Quando existir revisão de vídeo
 * de verdade, ela muda o TIPO aqui, e a mudança não passa despercebida.
 */
export const EditorialMediaCoverageSchema = z.discriminatedUnion('kind', [
  /**
   * Só a CAPA do Reel: o primeiro quadro, que é o que aparece na grade do
   * perfil. É cobertura PARCIAL, e a tela precisa dizer "capa".
   */
  z.strictObject({
    kind: z.literal('cover'),
    stills: z.literal(1),
    /** Quantas telas a peça tem ao todo. Nulo quando não foi medido. */
    screens: z.int().positive().nullable(),
    videoReviewed: z.literal(false),
  }),
  /** O JPEG que vai ao ar, que É a publicação inteira. Cobertura integral. */
  z.strictObject({
    kind: z.literal('photo'),
    stills: z.literal(1),
    videoReviewed: z.literal(false),
  }),
  /**
   * Amostras das telas do vídeo, com ordem e duração.
   *
   * `stills` é quantas imagens o crítico ABRIU, e `screens` quantas telas a peça
   * tem: os dois números juntos são o que impede "amostrou 2 de 5" de ser lido
   * como "viu a peça". `coveredMs` é a soma da duração das telas amostradas, e
   * `totalMs` a duração do vídeo.
   */
  z.strictObject({
    kind: z.literal('samples'),
    stills: z.int().positive(),
    screens: z.int().positive(),
    coveredMs: z.int().nonnegative(),
    totalMs: z.int().positive(),
    videoReviewed: z.literal(false),
  }),
  /**
   * Os SLIDES do carrossel, abertos contra os que a peça tem.
   *
   * `stills` é quantos o crítico abriu e `slides` quantos a peça tem, e os dois
   * juntos são a resposta ao que o plano pede: "ausência de um slide produz
   * cobertura incompleta explícita". Um parecer que abriu um slide de sete não
   * pode ser lido como parecer sobre a sequência, que é onde mora metade do
   * julgamento de um carrossel.
   *
   * Não há `coveredMs` nem `totalMs`: um carrossel não tem duração, e inventar
   * uma seria exatamente a "retenção por slide" que o plano proíbe.
   *
   * `videoReviewed` é `false` literal aqui pelo motivo mais simples de todos:
   * não existe vídeo nenhum nesta peça. O campo fica porque a união inteira o
   * carrega, e tirá-lo de um dos ramos obrigaria cada leitor a testar a
   * presença antes de ler.
   */
  z.strictObject({
    kind: z.literal('carousel'),
    stills: z.int().positive(),
    slides: z.int().positive(),
    videoReviewed: z.literal(false),
  }),
]);
export type EditorialMediaCoverage = z.infer<
  typeof EditorialMediaCoverageSchema
>;

/**
 * O que o portão de marca MEDIU, citado e não remedido.
 *
 * Ele entra no pedido como texto curto e fechado: o resultado do portão e os
 * dois números que o desenho da peça consulta. O crítico não recebe o relatório
 * inteiro porque ele não vai auditar a medição; ele vai julgar o que a medição
 * não alcança, sabendo o que já foi conferido.
 */
export interface EditorialBrandCitation {
  /** `pass`, `alert` ou `block`, como o portão respondeu. */
  outcome: string;
  code: string;
  /**
   * A MENOR razão de contraste entre os pares que o renderizador mediu.
   *
   * É resumo da medição que o portão consumiu, e não uma segunda medição: os
   * pares são os mesmos, e o mínimo é o número que o portão comparou com o piso.
   * Nulo quando não houve relatório de composição, que é o caso da peça de foto.
   */
  worstContrastRatio: number | null;
  /** Quantos pares de contraste foram medidos. Nulo é não houve relatório. */
  measuredPairs: number | null;
  /** Quantos elementos críticos foram medidos. Nulo é não houve relatório. */
  measuredBoxes: number | null;
}

export interface EditorialMediaReviewInput {
  /**
   * As imagens PARADAS que vão no pedido, na ordem em que aparecem na peça.
   *
   * Lista, e não um `still` só, desde 2026-09-06 (achado F11). O motivo é que a
   * pergunta do dono era sobre a peça e a resposta era sobre a capa: a segunda
   * dimensão da régua julga tempo de leitura, e um parecer que só viu o primeiro
   * quadro não tem como julgar a tela 3.
   *
   * Nunca vazia: sem imagem nenhuma não há chamada a fazer, e o serviço devolve
   * `NO_STILL` antes de chegar aqui.
   */
  stills: readonly EditorialMediaStill[];
  /**
   * O MAPA TEMPORAL da peça: quantas telas ela tem e quanto dura cada uma.
   *
   * Ele viaja mesmo quando nem toda tela virou amostra, e é isso que permite ao
   * pedido dizer ao crítico o que ele NÃO está vendo. `null` na peça de foto,
   * que não tem linha do tempo nenhuma.
   */
  timeline: {
    totalMs: number;
    screens: ReadonlyArray<{
      order: number;
      startMs: number;
      durationMs: number;
      screenText: string;
      /** Esta tela virou amostra e está entre as imagens do pedido? */
      sampled: boolean;
    }>;
  } | null;
  pieceKind: string;
  series: string;
  topicTitle: string;
  hook: string;
  screenTexts: readonly string[];
  caption: string;
  cta: string;
  brand: EditorialBrandCitation;
}

export const EditorialMediaReviewResultSchema = z.strictObject({
  provider: z.string().min(1).max(40),
  model: z.string().min(1).max(80),
  promptVersion: z.string().min(1).max(60),
  review: EditorialMediaReviewSchema,
  usage: GenerationUsageSchema,
  latencyMs: z.int().nonnegative(),
});
export type EditorialMediaReviewResult = z.infer<
  typeof EditorialMediaReviewResultSchema
>;

/**
 * O adaptador da revisão visual.
 *
 * Porta separada da do texto, e não um segundo método na mesma: a entrada
 * carrega BYTES de imagem, o modelo precisa ter visão, e o preço do token de
 * entrada é outro. Um adaptador só faria uma pilha sem modelo de visão
 * implementar metade da interface e falhar em execução.
 */
export interface EditorialMediaReviewer {
  readonly provider: string;
  reviewMedia(
    input: EditorialMediaReviewInput,
  ): Promise<EditorialMediaReviewResult>;
}

/**
 * O parecer VISUAL gravado, e a âncora que decide a validade dele.
 *
 * Ele mora na mesma tabela do parecer de texto, com `scope` diferente, porque
 * as duas coisas são a mesma espécie: uma opinião com versão de régua, custo e
 * recibo de chamada. O que muda é o que foi olhado, e é `scope` que conta isso.
 *
 * A validade depende de DOIS hashes, e essa é a decisão central:
 *
 * - `assetsSha256` cobre a mídia (o MP4 ou o JPEG) e a capa. Mídia regerada,
 *   parecer visual vencido.
 * - `contentSha256` cobre o texto. Ele entra porque a terceira dimensão julga
 *   COERÊNCIA entre gancho, imagem, legenda e CTA: um parecer que dissesse "a
 *   legenda combina com a imagem" continuaria pendurado numa legenda reescrita
 *   se só a mídia amarrasse.
 */
export const EditorialMediaOpinionSchema = z.strictObject({
  contentVersionId: UuidV4Schema,
  contentVersion: z.int().positive(),
  contentSha256: z.string().regex(/^[0-9a-f]{64}$/u),
  /** A versão da regra que produziu `contentSha256`. Ver `EditorialOpinion`. */
  contentHashVersion: z.int().positive(),
  /** Hash dos ativos avaliados. Ver `hashDosAtivosAvaliados`, no domínio. */
  assetsSha256: z.string().regex(/^[0-9a-f]{64}$/u),
  scope: z.literal('media'),
  mediaReviewed: z.literal(true),
  /** O papel da PRIMEIRA imagem aberta. `coverage` conta a história inteira. */
  mediaRole: z.enum(EDITORIAL_MEDIA_ROLES),
  /** O que foi olhado, e o que não foi. Ver `EditorialMediaCoverageSchema`. */
  coverage: EditorialMediaCoverageSchema,
  promptVersion: z.string().min(1).max(60),
  rubricVersion: z.string().min(1).max(60),
  provider: z.string().min(1).max(40),
  model: z.string().min(1).max(80),
  recommendation: EditorialRecommendationSchema,
  criteria: z
    .array(EditorialMediaCriterionSchema)
    .length(EDITORIAL_MEDIA_CRITERIA.length),
  findings: z.array(EditorialMediaFindingSchema).max(10),
  mainChange: z
    .string()
    .trim()
    .min(1)
    .max(EDITORIAL_PROSE_MAX_CHARS)
    .nullable(),
  pendency: EditorialPendencySchema.nullable(),
  total: z
    .int()
    .min(0)
    .max(EDITORIAL_MEDIA_CRITERIA.length * 4),
  meetsFloor: z.boolean(),
  calls: z.int().positive(),
  /** Micros. ANULÁVEL: preço do token de imagem não configurado é desconhecido. */
  costMicros: z.int().nonnegative().nullable(),
  latencyMs: z.int().nonnegative(),
  evaluatedAt: IsoUtcDateTimeSchema,
});
export type EditorialMediaOpinion = z.infer<typeof EditorialMediaOpinionSchema>;

/**
 * Por que a revisão visual não aconteceu, quando não aconteceu.
 *
 * Os sete primeiros são os mesmos do texto e significam o mesmo, `HASH_VERSION`
 * incluído: o parecer visual também carrega `contentHashVersion`.
 *
 * `NO_STILL` continua existindo e MUDOU DE ALCANCE em 2026-09-06.
 *
 * Até o lote 03 ele era o estado normal da Story: o renderizador só convertia o
 * primeiro quadro em JPEG para o Reel, e os quadros intermediários viviam num
 * diretório temporário apagado assim que o FFmpeg terminava. A Story ia ao ar
 * sem nenhuma imagem que alguém pudesse olhar.
 *
 * Agora o motor PERSISTE uma amostra por tela (`amostrasDaPeca`, em
 * `react-frame-engine.ts`), e a Story passa a ter imagem verificável. `NO_STILL`
 * fica para o que ele sempre deveria ter significado: esta peça não tem imagem
 * parada nenhuma, nem capa, nem foto, nem amostra. É o caso da peça renderizada
 * por uma versão anterior do motor, e o da peça sem mídia.
 *
 * As amostras NÃO entram no manifesto. Ver `EditorialMediaCoverageSchema`.
 */
export const EDITORIAL_MEDIA_UNAVAILABLE_REASONS = [
  'OFF',
  'STALE',
  'HASH_VERSION',
  'BUDGET_EXHAUSTED',
  'BUDGET_UNRECORDED',
  'FAILED',
  'NOT_REVIEWED',
  /** A peça não tem imagem parada nenhuma para olhar: nem capa, nem amostra. */
  'NO_STILL',
] as const;
export const EditorialMediaUnavailableReasonSchema = z.enum(
  EDITORIAL_MEDIA_UNAVAILABLE_REASONS,
);
export type EditorialMediaUnavailableReason = z.infer<
  typeof EditorialMediaUnavailableReasonSchema
>;

// ---------------------------------------------------------------------------
// O que a tela recebe
// ---------------------------------------------------------------------------

/**
 * Por que o parecer ausente carrega MOTIVO.
 *
 * Um campo anulável responderia "não tem parecer" e deixaria a tela decidir o
 * que fazer com isso, que é como um espaço em branco vira "deve estar tudo
 * certo" na cabeça de quem aprova. Com a união discriminada, a tela é obrigada a
 * desenhar o estado, e o estado diz por que não houve avaliação. É a mesma
 * doutrina de `MetricValueSchema`: indisponível não é zero, e aqui indisponível
 * não é aprovado.
 */
export const EDITORIAL_UNAVAILABLE_REASONS = [
  /** O modo está desligado. É o estado de hoje. */
  'OFF',
  /** Existe parecer, mas de um texto que não é mais este. */
  'STALE',
  /**
   * Existe parecer deste texto, medido por uma régua de hash ANTERIOR à atual.
   *
   * Ele não é `STALE`, e a diferença é a que o lote 03 deixou anotada e não
   * pôde consertar: `STALE` manda quem lê procurar o que mudou no texto, e aqui
   * o texto não mudou — mudou o cálculo que decide se ele mudou (achado F09,
   * 2026-09-06). Sem este valor, subir a régua faria o painel acusar de "texto
   * alterado" toda peça avaliada antes da subida, e quem investigasse procuraria
   * uma edição que nunca houve.
   */
  'HASH_VERSION',
  /**
   * O teto de chamadas do DIA acabou antes desta peça.
   *
   * Estado previsto: o dono configurou um teto e ele foi atingido. A reação é
   * olhar o gasto do dia, e não investigar defeito.
   */
  'BUDGET_EXHAUSTED',
  /**
   * A reserva do gasto NÃO pôde ser gravada, e por isso nada foi chamado.
   *
   * Estado de defeito, e o oposto do anterior: não é que o dia acabou, é que o
   * razão do orçamento parou de aceitar escrita, e chamar sem conseguir contar o
   * que se chamou é como o gasto cresce sem ninguém ver. `revisao-editorial.ts`
   * e `revisao-visual.ts` já separavam os dois no LOG desde o lote 03; o enum da
   * tela tinha um valor só para os dois, e o comentário de lá dizia que abrir o
   * segundo era deste lote.
   */
  'BUDGET_UNRECORDED',
  /** A chamada ao revisor falhou. O custo dela foi registrado assim mesmo. */
  'FAILED',
  /** A peça é anterior à revisão editorial, ou nunca passou por ela. */
  'NOT_REVIEWED',
] as const;
export const EditorialUnavailableReasonSchema = z.enum(
  EDITORIAL_UNAVAILABLE_REASONS,
);
export type EditorialUnavailableReason = z.infer<
  typeof EditorialUnavailableReasonSchema
>;

export const ContentEditorialSchema = z.discriminatedUnion('availability', [
  z.strictObject({
    availability: z.literal('available'),
    recommendation: EditorialRecommendationSchema,
    total: z
      .int()
      .min(0)
      .max(EDITORIAL_CRITERIA.length * 4),
    meetsFloor: z.boolean(),
    criteria: z
      .array(EditorialCriterionSchema)
      .length(EDITORIAL_CRITERIA.length),
    findings: z.array(EditorialFindingSchema).max(10),
    mainChange: z.string().min(1).max(EDITORIAL_PROSE_MAX_CHARS).nullable(),
    pendency: EditorialPendencySchema.nullable(),
    /** O texto que está na tela é uma reescrita. Ver `EditorialOpinionSchema`. */
    corrected: z.boolean(),
    /**
     * Quantas reescritas a peça recebeu. A tela precisa dos DOIS: "corrigida
     * uma vez" deixou de ser a única frase possível quando o laço de correção
     * entrou, e três tentativas com `corrected` falso é a peça em que todas
     * pioraram e o original ficou.
     */
    correctionAttempts: z.int().nonnegative(),
    scope: z.literal('text'),
    rubricVersion: z.string().min(1).max(60),
    promptVersion: z.string().min(1).max(60),
    model: z.string().min(1).max(80),
    evaluatedAt: IsoUtcDateTimeSchema,
  }),
  z.strictObject({
    availability: z.literal('unavailable'),
    reason: EditorialUnavailableReasonSchema,
  }),
]);
export type ContentEditorialDto = z.infer<typeof ContentEditorialSchema>;

/**
 * O parecer da MÍDIA, como a tela o recebe.
 *
 * Contrato SEPARADO do de texto, e este é o coração do achado F11: até
 * 2026-09-06 o painel recebia um campo `editorial` só, alimentado apenas pelo
 * parecer de TEXTO (`panel-snapshot-service.ts`, `projetarParecer`). O parecer
 * visual era gravado, custava dinheiro e nunca chegava a quem decide. Quem
 * abrisse a revisão individual lia "Parecer editorial" e supunha que ele falava
 * da peça inteira.
 *
 * Juntar os dois num campo só foi recusado por dois motivos, e nenhum é estilo:
 *
 * 1. **As réguas são diferentes.** O texto tem cinco dimensões e piso 16/20; a
 *    mídia tem três e piso próprio. Uma nota "14" sem dizer sobre quantos não
 *    significa nada, e um bloco com duas réguas somadas seria um número que não
 *    existe em régua nenhuma.
 * 2. **As âncoras são diferentes.** O parecer de texto vence quando o TEXTO
 *    muda; o visual vence quando a mídia OU o texto mudam. Fundidos, uma
 *    regeneração de mídia invalidaria a nota do texto, que continua válida.
 *
 * `coverage` é obrigatória no lado disponível: é ela que faz a tela dizer
 * "capa" quando foi só a capa. Ver `EditorialMediaCoverageSchema`.
 */
export const ContentMediaEditorialSchema = z.discriminatedUnion(
  'availability',
  [
    z.strictObject({
      availability: z.literal('available'),
      recommendation: EditorialRecommendationSchema,
      total: z
        .int()
        .min(0)
        .max(EDITORIAL_MEDIA_CRITERIA.length * 4),
      meetsFloor: z.boolean(),
      criteria: z
        .array(EditorialMediaCriterionSchema)
        .length(EDITORIAL_MEDIA_CRITERIA.length),
      findings: z.array(EditorialMediaFindingSchema).max(10),
      mainChange: z.string().min(1).max(EDITORIAL_PROSE_MAX_CHARS).nullable(),
      pendency: EditorialPendencySchema.nullable(),
      scope: z.literal('media'),
      coverage: EditorialMediaCoverageSchema,
      rubricVersion: z.string().min(1).max(60),
      promptVersion: z.string().min(1).max(60),
      model: z.string().min(1).max(80),
      evaluatedAt: IsoUtcDateTimeSchema,
    }),
    z.strictObject({
      availability: z.literal('unavailable'),
      reason: EditorialMediaUnavailableReasonSchema,
    }),
  ],
);
export type ContentMediaEditorialDto = z.infer<
  typeof ContentMediaEditorialSchema
>;
